import type { AgentScreen } from './screenIndex';
import type { AgentSkill } from './skills';
import { skillRowsAt } from './skills';

export type CapabilityField = {
  key: string;
  label: string;
  type: string;
  required: boolean;
};

export type CapabilityTarget = {
  route: string;
  screen: string;
  rowsStateKey: string;
  inputs: CapabilityField[];
  outputs: { key: string; label: string }[];
  requiredOutputs: string[];
  fixedConditions: Record<string, string>;
};

/** 모델은 업무 도구를 고르고, executor만 그 안의 화면/DOM 계약을 다룬다. */
export type CapabilityTool = {
  id: string;
  description: string;
  targets: CapabilityTarget[];
};

type Contract = Pick<AgentSkill, 'description' | 'preferredRoutes' | 'rowsStateKey'
| 'rowsByRoute' | 'requiredColumns' | 'conditions'>;

function targetOf(contract: Contract, screen: AgentScreen): CapabilityTarget | undefined {
  const rowsStateKey = skillRowsAt(contract, screen.route);
  if (!rowsStateKey) return undefined;
  const grid = screen.grids.find((candidate) => candidate.key === rowsStateKey);
  if (!grid) return undefined;
  const available = new Set(grid.columns.map((column) => column.id));
  if ((contract.requiredColumns ?? []).some((column) => !available.has(column))) return undefined;
  return {
    route: screen.route,
    screen: screen.name,
    rowsStateKey,
    inputs: screen.fields.map((field) => ({
      key: field.key,
      label: field.label ?? field.key,
      type: field.widget,
      // 현재 카탈로그는 검색 필드의 필수 여부를 보존하지 않는다. executor의
      // 실제 DOM 검증 전까지 필수라고 지어내지 않는다.
      required: false,
    })),
    outputs: grid.columns.map((column) => ({ key: column.id, label: column.text ?? column.id })),
    requiredOutputs: contract.requiredColumns ?? [],
    fixedConditions: contract.conditions ?? {},
  };
}

function add(
  tools: Map<string, CapabilityTool>,
  ids: string[],
  contract: Contract,
  screens: AgentScreen[],
): void {
  const targets = (contract.preferredRoutes ?? []).flatMap((route) => {
    const screen = screens.find((candidate) => candidate.route === route);
    const target = screen && targetOf(contract, screen);
    return target ? [target] : [];
  });
  for (const id of ids) {
    const previous = tools.get(id);
    tools.set(id, {
      id,
      description: contract.description,
      targets: [...(previous?.targets ?? []), ...targets].filter((target, index, all) => (
        all.findIndex((candidate) => candidate.route === target.route
          && candidate.rowsStateKey === target.rowsStateKey) === index
      )),
    });
  }
}

export function buildCapabilityTools(skills: AgentSkill[], screens: AgentScreen[]): CapabilityTool[] {
  const tools = new Map<string, CapabilityTool>();
  for (const skill of skills) {
    if (skill.kind === 'artifact') continue;
    add(tools, skill.capabilities ?? [], skill, screens);
    for (const variant of skill.variants ?? []) {
      add(tools, variant.capabilities ?? [], {
        description: [skill.description, variant.instructions].filter(Boolean).join(' — '),
        preferredRoutes: variant.preferredRoutes ?? skill.preferredRoutes,
        rowsStateKey: variant.rowsStateKey ?? skill.rowsStateKey,
        // 변형이 표를 따로 정했으면 화면별 표는 물려받지 않는다.
        rowsByRoute: variant.rowsStateKey ? undefined : skill.rowsByRoute,
        requiredColumns: variant.requiredColumns ?? skill.requiredColumns,
        conditions: variant.conditions ?? skill.conditions,
      }, screens);
    }
  }
  return Array.from(tools.values());
}

export function findCapabilityTool(capability: string, tools: CapabilityTool[]): CapabilityTool | undefined {
  return tools.find((tool) => tool.id === capability);
}

export function describeCapabilityTool(tool: CapabilityTool | undefined): string {
  if (!tool) return '';
  const targets = tool.targets.map((target) => (
    `- ${target.screen} (${target.route}), 결과표 ${target.rowsStateKey}\n`
    + `  입력: ${target.inputs.map((field) => `${field.label}[${field.key}:${field.type}]`).join(', ') || '없음'}\n`
    + `  필수 출력: ${target.requiredOutputs.join(', ') || '없음'}\n`
    + `  사용 가능한 출력: ${target.outputs.map((field) => `${field.label}[${field.key}]`).join(', ')}`
  )).join('\n');
  return `\n선택된 업무 도구: ${tool.id} — ${tool.description}\n${targets || '- 실행 가능한 화면 계약 없음'}`;
}

export function validateCapabilityPlan(
  tool: CapabilityTool | undefined,
  plan: { kind: string; retrievals?: { route: string; rowsStateKey: string; requiredColumns?: string[] }[] },
): void {
  if (!tool || plan.kind !== 'retrieve') return;
  // **계약이 비어 있으면 판정할 것이 없다.** 스킬이 결과 표를 안 적어 두면 도구에
  // 화면 계약이 하나도 안 붙는데, 그때 여기서 거절하면 그 업무는 **무슨 계획을 세워도**
  // 막힌다 — `필요: .` 이라는 말이 안 되는 문구를 남기고 턴이 죽었다.
  if (!tool.targets.length) return;
  const retrievals = plan.retrievals ?? [];
  const matched = retrievals.some((retrieval) => tool.targets.some((target) => (
    target.route === retrieval.route
    && target.rowsStateKey === retrieval.rowsStateKey
    && target.requiredOutputs.every((column) => retrieval.requiredColumns?.includes(column))
  )));
  if (matched) return;
  // 무엇이 어긋났는지 적는다. `계약을 충족하지 않았다` 만으로는 계획을 다시 세울 수도,
  // 사람이 고칠 수도 없다 — 실패 하나를 놓고 스킬과 카탈로그를 손으로 대조해야 했다.
  const wanted = tool.targets
    .map((target) => `${target.route}/${target.rowsStateKey}(${target.requiredOutputs.join('·')})`)
    .join(' 또는 ');
  const got = retrievals.length
    ? retrievals
      .map((retrieval) => `${retrieval.route}/${retrieval.rowsStateKey}(${(retrieval.requiredColumns ?? []).join('·')})`)
      .join(', ')
    : '조회 없음';
  throw new Error(`${tool.id} 업무 도구의 계약을 계획이 충족하지 않았다. 필요: ${wanted}. 계획: ${got}`);
}
